import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@kss/api';
import { Alert, Button, Card, Spinner } from '@kss/ui';
import { ArrowLeft, Download, RefreshCw } from 'lucide-react';

/**
 * Party-wise report (Customer + Supplier summary).
 *
 * The ReportBuilder is single-resource with no group-by, so a "total per
 * customer / supplier" summary wasn't possible. This page groups every
 * SalesInvoice by customer and every PurchaseInvoice by supplier into a
 * sortable, exportable ledger summary.
 */

interface PartyRow {
  key: string;
  name: string;
  count: number;
  total: number;     // sales (customers) / purchases (suppliers)
  paid: number;
  outstanding: number;
}

type SortKey = 'name' | 'count' | 'total' | 'paid' | 'outstanding';

const inr = (n: number) => (Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const pickList = (d: unknown): Array<Record<string, unknown>> => {
  if (Array.isArray(d)) return d as Array<Record<string, unknown>>;
  const o = d as { items?: unknown[]; data?: unknown[] | { items?: unknown[] } } | null;
  if (Array.isArray(o?.items)) return o!.items as Array<Record<string, unknown>>;
  if (Array.isArray(o?.data)) return o!.data as Array<Record<string, unknown>>;
  if (o?.data && Array.isArray((o.data as { items?: unknown[] }).items)) return (o.data as { items: unknown[] }).items as Array<Record<string, unknown>>;
  return [];
};

function groupBy(
  rows: Array<Record<string, unknown>>,
  idKey: string,
  nameKey: string,
): PartyRow[] {
  const map = new Map<string, PartyRow>();
  for (const r of rows) {
    const id = r[idKey];
    const name = String(r[nameKey] ?? '') || (id != null ? `#${id}` : 'Unassigned');
    const key = String(id ?? name);
    const grand = Number(r.grandTotal ?? r.amount ?? 0) || 0;
    const paid = Number(r.paidAmount ?? 0) || 0;
    const bal = r.balance != null ? Number(r.balance) || 0 : Math.max(0, grand - paid);
    const cur = map.get(key) ?? { key, name, count: 0, total: 0, paid: 0, outstanding: 0 };
    cur.count += 1;
    cur.total += grand;
    cur.paid += paid;
    cur.outstanding += bal;
    map.set(key, cur);
  }
  return [...map.values()];
}

export default function PartyWiseReport() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<'customers' | 'suppliers'>('customers');
  const [customers, setCustomers] = useState<PartyRow[]>([]);
  const [suppliers, setSuppliers] = useState<PartyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('outstanding');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  async function load() {
    setLoading(true); setErr(null);
    try {
      const [si, pi] = await Promise.all([
        apiClient.get('/sales/SalesInvoice').then((r) => pickList(r.data)).catch(() => []),
        apiClient.get('/purchase/PurchaseInvoice').then((r) => pickList(r.data)).catch(() => []),
      ]);
      setCustomers(groupBy(si, 'customerId', 'customerName'));
      setSuppliers(groupBy(pi, 'supplierId', 'supplierName'));
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { void load(); }, []);

  const rows = tab === 'customers' ? customers : suppliers;
  const sorted = useMemo(() => {
    const arr = [...rows];
    arr.sort((a, b) => {
      const av = a[sortKey]; const bv = b[sortKey];
      const cmp = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv));
      return sortDir === 'desc' ? -cmp : cmp;
    });
    return arr;
  }, [rows, sortKey, sortDir]);

  const totals = useMemo(() => sorted.reduce(
    (t, r) => ({ count: t.count + r.count, total: t.total + r.total, paid: t.paid + r.paid, outstanding: t.outstanding + r.outstanding }),
    { count: 0, total: 0, paid: 0, outstanding: 0 },
  ), [sorted]);

  function toggleSort(k: SortKey) {
    if (sortKey === k) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    else { setSortKey(k); setSortDir(k === 'name' ? 'asc' : 'desc'); }
  }

  function exportCsv() {
    const head = tab === 'customers'
      ? ['Customer', 'Invoices', 'Total Sales', 'Paid', 'Outstanding']
      : ['Supplier', 'Bills', 'Total Purchases', 'Paid', 'Outstanding'];
    const lines = [head.join(',')];
    for (const r of sorted) {
      lines.push([`"${r.name.replace(/"/g, '""')}"`, r.count, r.total.toFixed(2), r.paid.toFixed(2), r.outstanding.toFixed(2)].join(','));
    }
    lines.push(['Total', totals.count, totals.total.toFixed(2), totals.paid.toFixed(2), totals.outstanding.toFixed(2)].join(','));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `party-wise-${tab}.csv`; a.click();
    URL.revokeObjectURL(url);
  }

  const totalLabel = tab === 'customers' ? 'Total Sales' : 'Total Purchases';
  const countLabel = tab === 'customers' ? 'Invoices' : 'Bills';
  const nameLabel = tab === 'customers' ? 'Customer' : 'Supplier';

  return (
    <div className="space-y-3 p-1">
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-surface-card px-4 py-3">
        <button onClick={() => navigate('/panel/reports')} className="grid h-8 w-8 place-items-center rounded border border-border hover:border-primary" aria-label="Back">
          <ArrowLeft size={16} />
        </button>
        <div className="min-w-0 flex-1">
          <h1 className="text-base font-semibold text-content-primary">Party-wise report</h1>
          <p className="text-xs text-content-muted">Sales by customer · purchases by supplier — totals, paid &amp; outstanding</p>
        </div>
        <div className="inline-flex overflow-hidden rounded-md border border-border">
          {(['customers', 'suppliers'] as const).map((t) => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-3 py-1.5 text-xs font-medium ${tab === t ? 'bg-primary text-white' : 'bg-surface-card text-content-secondary hover:bg-surface-card-hover'}`}>
              {t === 'customers' ? 'Customers' : 'Suppliers'}
            </button>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Reload
        </Button>
        <Button variant="outline" size="sm" onClick={exportCsv} disabled={!sorted.length}>
          <Download className="h-3.5 w-3.5" /> Export CSV
        </Button>
      </div>

      {err && <Alert variant="danger">{err}</Alert>}

      <Card className="p-0">
        {loading ? (
          <div className="flex items-center gap-2 p-6 text-sm text-content-muted"><Spinner /> Loading…</div>
        ) : sorted.length === 0 ? (
          <div className="p-6 text-center text-sm text-content-muted">No {tab} data yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface-card-hover text-left text-[11px] uppercase tracking-wide text-content-muted">
                <tr>
                  <Th label={nameLabel} k="name" cur={sortKey} dir={sortDir} on={toggleSort} />
                  <Th label={countLabel} k="count" cur={sortKey} dir={sortDir} on={toggleSort} right />
                  <Th label={totalLabel} k="total" cur={sortKey} dir={sortDir} on={toggleSort} right />
                  <Th label="Paid" k="paid" cur={sortKey} dir={sortDir} on={toggleSort} right />
                  <Th label="Outstanding" k="outstanding" cur={sortKey} dir={sortDir} on={toggleSort} right />
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => (
                  <tr key={r.key} className="border-t border-border hover:bg-surface-card-hover/40">
                    <td className="px-3 py-2 font-medium text-content-primary">{r.name}</td>
                    <td className="px-3 py-2 text-right">{r.count}</td>
                    <td className="px-3 py-2 text-right">₹ {inr(r.total)}</td>
                    <td className="px-3 py-2 text-right text-success">₹ {inr(r.paid)}</td>
                    <td className={`px-3 py-2 text-right font-medium ${r.outstanding > 0 ? 'text-danger' : 'text-content-muted'}`}>₹ {inr(r.outstanding)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-border bg-surface-card-hover/40 font-semibold">
                  <td className="px-3 py-2">Total ({sorted.length})</td>
                  <td className="px-3 py-2 text-right">{totals.count}</td>
                  <td className="px-3 py-2 text-right">₹ {inr(totals.total)}</td>
                  <td className="px-3 py-2 text-right text-success">₹ {inr(totals.paid)}</td>
                  <td className="px-3 py-2 text-right text-danger">₹ {inr(totals.outstanding)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function Th({ label, k, cur, dir, on, right }: { label: string; k: SortKey; cur: SortKey; dir: 'asc' | 'desc'; on: (k: SortKey) => void; right?: boolean }) {
  return (
    <th className={`cursor-pointer select-none px-3 py-2 ${right ? 'text-right' : 'text-left'}`} onClick={() => on(k)}>
      {label}{cur === k ? (dir === 'desc' ? ' ↓' : ' ↑') : ''}
    </th>
  );
}
